import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, catchError, Observable, of, shareReplay, tap } from "rxjs";
import { environment } from "../../../environments/environment";
import { MESSAGEWAIT4SIGN, REFRESH_TOKEN, TOKEN } from "../constants/localstorage.constants";
import { CONNEXION } from "../constants/route.constants";
import { USE_COOKIES_FALSE, USE_COOKIES_TRUE } from "../constants/url.constants";
import { LoginResponse } from "../models/login-response.model";
import { MFAResponse } from "../models/mfa-response.model";
import { MsalAuthenticationService } from "./msal-authentication.service";

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  private authenticationUrl = "authentication";
  private baseUrl: string;

  private emailSource = new BehaviorSubject<string>("");
  currentEmail = this.emailSource.asObservable();

  private passwordSource = new BehaviorSubject<string>("");
  currentPassword = this.passwordSource.asObservable();

  constructor(
    private http: HttpClient,
    private route: Router,
    private msalAuthenticationService: MsalAuthenticationService
  ) {
    this.baseUrl = environment.apiConfig.uri + this.authenticationUrl;
  }

  public get tokenValue() {
    return new BehaviorSubject<LoginResponse>(JSON.parse(localStorage.getItem(TOKEN)!));
  }

  checkRememberMeToken(): Observable<string> {
    const url = this.baseUrl + "/twoFactor/rememberMeToken";
    return this.http.get<string>(url, { withCredentials: true }).pipe(
      catchError(error => {
        if (error.status === 404) {
          //no token found in cookies
          return of("");
        }
        throw new Error(error);
      })
    );
  }

  sendLogin(email: string, password: string, twoFactorCode: string, useCookies: boolean): Observable<LoginResponse> {
    const body = {
      email: email,
      password: password,
      twoFactorCode: twoFactorCode,
    };
    let url = this.baseUrl + "/login";
    if (useCookies) {
      url += USE_COOKIES_TRUE;
    } else {
      url += USE_COOKIES_FALSE;
    }

    return this.http.post<LoginResponse>(url, body, { withCredentials: true }).pipe(
      shareReplay(),
      tap(response => this.loginUser(response, useCookies))
    );
  }

  setPassword(email: string, resetCode: string, password: string) {
    const body = {
      email: email,
      resetCode: resetCode,
      newPassword: password,
    };
    return this.http.post(this.baseUrl + "/resetPassword", body);
  }

  confirmEmail(userId: string, code: string, email: string) {
    let query = new HttpParams();
    if (userId) {
      query = query.append("userId", userId);
    }
    if (code) {
      query = query.append("code", code);
    }
    if (email) {
      query = query.append("changeEmail", email);
    }

    return this.http.get(this.baseUrl + "/confirmEmail", { params: query });
  }

  refreshToken(): Observable<LoginResponse> {
    const refreshToken = JSON.parse(localStorage.getItem(REFRESH_TOKEN)!);
    const body = {
      refreshToken: refreshToken,
    };
    return this.http.post<LoginResponse>(this.baseUrl + "/refresh", body);
  }

  loginUser(token: LoginResponse, useCookies: boolean) {
    if (!useCookies) {
      localStorage.setItem(TOKEN, JSON.stringify(token.accessToken));
      localStorage.setItem(REFRESH_TOKEN, JSON.stringify(token.refreshToken));
    }
    this.msalAuthenticationService.clearFlags();
    localStorage.removeItem(MESSAGEWAIT4SIGN);
  }

  isLoggedIn(): boolean {
    const user = this.tokenValue.value;
    return user ? true : false;
  }

  logout() {
    localStorage.removeItem(TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem(MESSAGEWAIT4SIGN);
    this.msalAuthenticationService.clearFlags();
    this.route.navigate([CONNEXION]);
  }

  sendMFACode(code: string) {
    const body = {
      enable: true,
      twoFactorCode: code,
      resetSharedKey: true,
      resetRecoveryCodes: true,
      forgetMachine: true,
    };
    return this.http.post<MFAResponse>(this.baseUrl + "/manage/2fa", body);
  }

  sendToken(email: string) {
    let query = new HttpParams();
    if (email) {
      query = query.append("email", email);
    }
    return this.http.post(this.baseUrl + "/twoFactor/sendToken", {}, { params: query });
  }

  verifyToken(email: string, token: string) {
    let query = new HttpParams();
    if (email) {
      query = query.append("email", email);
      query = query.append("token", token);
    }
    return this.http.post(this.baseUrl + "/twoFactor/verifyToken", {}, { params: query });
  }

  sharedEmail(email: string) {
    this.emailSource.next(email);
  }

  sharedPassword(password: string) {
    this.passwordSource.next(password);
  }
}
